import axios from "axios";
import API_BASE_URL from "../../config/api";

const API_ENDPOINT = `${API_BASE_URL}/rejestrWnioskow`; // Zmienna przechowująca adres API
const getHeaders = async () => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Token is not available");
  }
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
};

export const createWniosek = async (valuesArray) => {
  try {
    const headers = await getHeaders();

    // Wprowadzenie zmiany do zapytania SQL
    const query = `
      INSERT INTO wnioski (
        data_SAD, nr_MRN, znak_pisma, nr_akt, nadawca_odbiorca, odbiorca, podpisany, rodzaj_SAD, procedura, platnik, powod_wpisania, sciezkaDoPliku, data_wpisania, unique_number, status, duplikat, createdAt, utworzony_przez, data_odebrania, data_terminu_odpowiedzi
      ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
    `;

    const createdWnioski = [];

    // Przygotowanie operacji zapisu dla wszystkich wartości
    for (const values of valuesArray) {
      const response = await axios.post(
        API_ENDPOINT + "/create",
        { values, query },
        { headers },
      );

      // Dodaj informacje o utworzonym wniosku
      if (response.data.id && response.data.unique_number) {
        createdWnioski.push({
          id: response.data.id,
          unique_number: response.data.unique_number,
          originalFullName: values[6], // podpisany jest na pozycji 6
        });
      }
    }

    return { status: 200, createdWnioski };
  } catch (error) {
    throw new Error("Błąd podczas tworzenia wniosku: " + error.message);
  }
};

export const getExistingMRNs = async () => {
  try {
    const headers = await getHeaders();
    const response = await axios.get(API_ENDPOINT + "/existing-mrns", {
      headers,
    });
    return response.data; // Zakładam, że zwracasz listę numerów MRN w formacie tablicy
  } catch (error) {
    throw new Error("Błąd podczas pobierania MRN: " + error.message);
  }
};

export const fetchWnioskiWithHistory = async () => {
  try {
    const headers = await getHeaders();

    // Pobierz dane
    const wnioskiResponse = await axios.get(API_ENDPOINT, { headers });
    const historyResponse = await axios.get(API_ENDPOINT + "/history", {
      headers,
    });
    const commentsResponse = await axios.get(API_ENDPOINT + "/commentsAll", {
      headers,
    });

    // Upewnij się, że dane są tablicami
    const wnioski = Array.isArray(wnioskiResponse.data)
      ? wnioskiResponse.data
      : [];
    const history = Array.isArray(historyResponse.data)
      ? historyResponse.data
      : [];
    const comments = Array.isArray(commentsResponse.data)
      ? commentsResponse.data
      : [];

    // Mapowanie danych z historią i komentarzami
    const wnioskiWithHistoryAndComments = wnioski.map((wniosek) => ({
      ...wniosek,
      history: history.filter((change) => change.custom_id === wniosek.id),
      comments: comments.filter((comment) => comment.customId === wniosek.id),
    }));

    return { data: wnioskiWithHistoryAndComments };
  } catch (error) {
    console.error("Error fetching wnioski with history and comments:", error);
    throw new Error(
      "Błąd podczas pobierania danych o wnioskach: " + error.message,
    );
  }
};

export const updateWniosek = async (wniosek) => {
  try {
    const response = await axios.post(API_ENDPOINT + "/create", { wniosek });
    return response.data;
  } catch (error) {
    throw new Error("Błąd podczas tworzenia wniosku: " + error.message);
  }
};

//*******************************************************EDIT FUNCTIONS */

const headers = {
  "Content-Type": "application/json",
  // Getter: token czytany przy każdym żądaniu, a nie raz przy ładowaniu skryptu
  // (inaczej po zalogowaniu bez odświeżenia strony wysyłany byłby stary/pusty token).
  get Authorization() {
    return `Bearer ${localStorage.getItem("token")}`;
  },
};

// Fetch row data by ID
export const fetchRowData = async (id) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/rejestrWnioskow/${id}`, {
      headers: headers,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching row data:", error);
    throw error;
  }
};

// Fetch comments by ID
export const fetchComments = async (id) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/rejestrWnioskow/comments/${id}`,
      {
        headers: headers,
      },
    );
    if (response.status === 200) {
      return response.data;
    } else {
      console.error("Failed to fetch comments for id:", id);
    }
  } catch (error) {
    console.error("Error fetching comments:", error);
    throw error;
  }
};

// Add a new comment
export const addComment = async (id, commentData) => {
  try {
    const headers = await getHeaders();
    const response = await axios.post(
      `${API_BASE_URL}/rejestrWnioskow/comments/${id}`,
      commentData,
      {
        headers: headers,
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error adding comment:", error);
    throw error;
  }
};

// Update field in a record
export const updateField = async (id, fieldData) => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/rejestrWnioskow/update/${id}`,
      fieldData,
      {
        headers: headers,
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error updating data:", error);
    throw error;
  }
};

// Save history for a record change
export const saveHistory = async (historyData) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/rejestrWnioskow/history`,
      historyData,
      {
        headers: headers,
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error saving history data:", error);
    throw error;
  }
};

// Log history
export const logHistory = async (id, historyData) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/history/${id}`,
      historyData,
      {
        headers: headers,
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error logging history:", error);
    throw error;
  }
};

export const mapPodpisanyToUsername = (podpisany) => {
  switch (podpisany) {
    case "DOBROWOLSKA-MITRĘGA ANNA":
      return "adobrowolska";
    case "CZERNIKOWSKA DOMINIKA":
      return "dczernikowska";
    case "BRUŚ BARBARA":
      return "bbrus";
    case "MUNIK PIOTR":
      return "pmunik";
    case "MAZUR SABINA":
      return "smazur";
    case "NOWAK PRZEMYSŁAW":
      return "pnowak";
    case "WOJTAŚ MILENA":
      return "mwojtas";
    case "REDUCHA KATARZYNA":
      return "kreducha";
    case "SZKUTNIK AGNIESZKA":
      return "aszkutnik";
    case "ŁOMŻA PRZEMYSŁAW":
      return "plomza";
    case "SKOLIMOWSKA MAGDALENA":
      return "mskolimowska";
    case "CIŻEWSKA JUSTYNA":
      return "jcizewska";
    case "PUZYR GRZEGORZ":
      return "gpuzyr";
    case "ŁOPATECKA MARTA":
      return "mlopatecka";
    default:
      return "";
  }
};

export const deleteWniosek = async (id) => {
  try {
    const headers = await getHeaders();
    const response = await axios.delete(`${API_ENDPOINT}/${id}`, { headers });
    return response; // Zwracaj całą odpowiedź HTTP
  } catch (error) {
    console.error("Error deleting wniosek:", error);
    throw new Error("Błąd podczas usuwania wniosku: " + error.message);
  }
};

export const fetchPowodWpisaniaOptions = async () => {
  try {
    const headers = await getHeaders();
    const response = await axios.get(`${API_BASE_URL}/list/powodWpisania`, {
      headers,
    });
    return response.data; // Zakładam, że dane to tablica powodów
  } catch (error) {
    console.error("Error fetching powod wpisania options:", error);
    throw new Error(
      "Błąd podczas pobierania powodów wpisania: " + error.message,
    );
  }
};

export const fetchUsers = async () => {
  try {
    const headers = await getHeaders();
    const response = await axios.get(`${API_BASE_URL}/list/uzytkownicy`, {
      headers,
    });
    return response.data; // Zakładam, że dane to tablica powodów
  } catch (error) {
    console.error("Error fetching Users:", error);
    throw new Error("Błąd podczas pobierania użytkowników: " + error.message);
  }
};

const mapApiDataToSortedNames = (data) =>
  Array.isArray(data)
    ? Array.from(
        new Set(data.map((item) => item?.name?.trim()).filter(Boolean)),
      ).sort((a, b) => a.localeCompare(b, "pl", { sensitivity: "base" }))
    : [];

export const fetchNadawcyOptions = async () => {
  try {
    const headers = await getHeaders();
    const response = await axios.get(`${API_BASE_URL}/list/eksportereksport`, {
      headers,
    });
    return mapApiDataToSortedNames(response.data);
  } catch (error) {
    console.error("Error fetching Nadawcy:", error);
    throw new Error("Błąd podczas pobierania nadawców: " + error.message);
  }
};

export const fetchOdbiorcyOptions = async () => {
  try {
    const headers = await getHeaders();
    const response = await axios.get(`${API_BASE_URL}/list/importerimport`, {
      headers,
    });
    return mapApiDataToSortedNames(response.data);
  } catch (error) {
    console.error("Error fetching Odbiorcy:", error);
    throw new Error("Błąd podczas pobierania odbiorców: " + error.message);
  }
};

export const saveReminderStatus = async (
  id,
  reminderSent,
  legalDeadlineChecked,
  legalDeadlineDate,
  pytanieZUrzedu,
  pytanieZUrzeduTimestamp,
  odpowiedzNaPytanie,
  odpowiedzNaPytanieTimestamp,
  doreczenieNieDotyczy,
  powodBrakuDoreczenia,
) => {
  try {
    const headers = await getHeaders();

    // Pobierz czas w strefie czasowej Warsaw
    const warsawTime = new Date()
      .toLocaleString("sv-SE", {
        timeZone: "Europe/Warsaw",
        hour12: false,
      })
      .replace(" ", "T");

    const payload = {
      wyslano_przypomnienie: reminderSent ? 1 : 0,
      termin_ustawowy_odpowiedzi: legalDeadlineChecked ? 1 : 0,
      pytanie_z_urzedu: pytanieZUrzedu ? 1 : 0,
      odpowiedz_na_pytanie: odpowiedzNaPytanie ? 1 : 0,
      doreczenie_nie_dotyczy: doreczenieNieDotyczy ? 1 : 0,
      powod_braku_doreczenia:
        doreczenieNieDotyczy && String(powodBrakuDoreczenia || "").trim()
          ? String(powodBrakuDoreczenia).trim()
          : null,
    };

    // Jeśli zaznaczono "Wysłano przypomnienie", zapisz datę i godzinę
    if (reminderSent) {
      payload.updated_at = warsawTime;
    }

    // Dodaj datę terminu ustawowego tylko jeśli checkbox jest zaznaczony
    if (legalDeadlineChecked && legalDeadlineDate) {
      payload.data_terminu_odpowiedzi = legalDeadlineDate; // np. "2025-07-20"
    } else if (!legalDeadlineChecked) {
      // Jeśli termin ustawowy jest odznaczony, wyczyść datę
      payload.data_terminu_odpowiedzi = null;
    }

    // Dodaj timestamp pytania z urzędu jeśli checkbox jest zaznaczony
    if (pytanieZUrzedu && pytanieZUrzeduTimestamp) {
      payload.data_pytania_z_urzedu = pytanieZUrzeduTimestamp; // np. "2025-07-20T14:30:00"
    } else if (!pytanieZUrzedu) {
      // Jeśli pytanie z urzędu jest odznaczone, wyczyść datę
      payload.data_pytania_z_urzedu = null;
    }

    // Dodaj timestamp odpowiedzi na pytanie jeśli checkbox jest zaznaczony
    if (odpowiedzNaPytanie && odpowiedzNaPytanieTimestamp) {
      payload.data_odpowiedzi_na_pytanie = odpowiedzNaPytanieTimestamp; // np. "2025-07-20T14:30:00"
      // Jeśli odpowiedź na pytanie jest zaznaczona, usuń powiązane pola
      payload.data_pytania_z_urzedu = null;
      payload.pytanie_z_urzedu = 0;
      payload.termin_ustawowy_odpowiedzi = 0;
      payload.data_terminu_odpowiedzi = null;
    }

    const response = await axios.put(
      `${API_BASE_URL}/rejestrWnioskow/update/${id}`,
      payload,
      { headers },
    );

    return response.data;
  } catch (error) {
    console.error("Error saving reminder status:", error);
    throw error;
  }
};

// Funkcja do masowego dodania komentarza do wybranych wniosków
export const addCommentToMultipleWnioski = async (ids, comment) => {
  try {
    const headers = await getHeaders();
    const response = await axios.post(
      `${API_ENDPOINT}/wnioski/comments/bulk`,
      { ids, comment },
      { headers },
    );
    return response.data;
  } catch (error) {
    console.error("Błąd przy dodawaniu komentarza do wielu wniosków:", error);
    throw error;
  }
};

export const addCommentWithNotification = async (data) => {
  try {
    const headers = await getHeaders();
    const response = await axios.post(
      `${API_ENDPOINT}/wnioski/comments/addWithNotification`,
      data,
      { headers },
    );
    return response.data;
  } catch (error) {
    if (error.response) {
      console.error("Response error:", {
        status: error.response.status,
        data: error.response.data,
      });
    } else if (error.request) {
      console.error(
        "Request error – brak odpowiedzi z serwera:",
        error.request,
      );
    } else {
      console.error("Inny błąd:", error.message);
    }
    throw error;
  }
};

export const fetchWniosekByUniqueNumber = async (uniqueNumber) => {
  try {
    const headers = await getHeaders();
    const response = await axios.get(
      `${API_BASE_URL}/rejestrWnioskow/by-unique-number/${uniqueNumber}`,
      { headers },
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching wniosek by unique number:", error);
    throw error;
  }
};
